import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Icon, SpinnerLoader, Status, Tooltip, Typography } from 'shared/ui'
import { Table, type IColumn } from 'shared/ui/TableNew'
import { CalendarDropdown } from 'shared/ui/CalendarDropdown/CalendarDropdown'
import { Select } from 'shared/ui/Select/Select'
import { getAriaLabel } from 'shared/lib'
import { DayjsFormats } from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { PaymentHistoryStore } from './store/paymentHistoryStore'
import { type PaymentHistoryRow } from './model/PaymentHistoryRow'
import { paymentStatusConfig, type PaymentStatusKey } from './config'
import styles from './styles.module.scss'

const formatAmount = (amountCents: number): string => {
  const amount = amountCents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount)
}

const FAILED_STATUS_TOOLTIP_DEFAULT = 'Payment failed. Please check your payment method.'

type IFailedStatusCellProps = {
  failureMessage?: string | null
}

const FailedStatusCell = ({ failureMessage }: IFailedStatusCellProps) => (
  <div
    className={classNames(styles.failedStatusCell)}
    aria-label={getAriaLabel('Status', 'Failed')}
  >
    <Icon icon='closeCircle' fontSize={14} color='var(--red-60)' />
    <div className={styles.failedStatusCellContent}>
      <Typography variant='body-sm-medium' color='var(--red-90)' ariaLabel='title'>
        Failed
      </Typography>
      <Tooltip label={failureMessage || FAILED_STATUS_TOOLTIP_DEFAULT} placement='top' breakWord>
        <span className={styles.failedStatusTrailingIcon}>
          <Icon icon='question' fontSize={12} />
        </span>
      </Tooltip>
    </div>
  </div>
)

export const PaymentHistory = observer(() => {
  const { usersStore } = useEntitiesContext()
  const store = useMemo(
    () => new PaymentHistoryStore(usersStore.user?.createdAt),
    [usersStore.user?.createdAt]
  )

  useEffect(() => {
    return () => store.dispose()
  }, [store])

  const columns: IColumn<PaymentHistoryRow>[] = [
    {
      field: 'status',
      name: 'Status',
      width: '120px',
      renderRowCell: (row) => {
        const statusProps = paymentStatusConfig[row.status as PaymentStatusKey]
        if (!statusProps) return null

        if (row.status === 'failed') {
          return (
            <div className={styles.statusCell}>
              <FailedStatusCell failureMessage={row.failureMessage} />
            </div>
          )
        }

        return (
          <div className={styles.statusCell}>
            <Status size='medium' {...statusProps} />
          </div>
        )
      },
    },
    {
      field: 'date',
      name: 'Date',
      width: '180px',
      renderRowCell: (row) => (
        <Typography ariaLabel='payment_date' variant='body-md-regular'>
          {uiStore.dayjs(row.createdAt).format(DayjsFormats.fullWithAtDash2)}
        </Typography>
      ),
    },
    {
      field: 'amount',
      name: 'Amount',
      width: '110px',
      renderRowCell: (row) => (
        <Typography ariaLabel='payment_amount' variant='body-md-regular'>
          {formatAmount(row.amount)}
        </Typography>
      ),
    },
    {
      field: 'method',
      name: 'Method',
      renderRowCell: (row) => {
        const isCard = row.icon !== 'bank'
        return (
          <div className={styles.methodCell}>
            <Icon
              icon={row.icon}
              fontSize={isCard ? 32 : 20}
              className={isCard ? '' : styles.methodIconAch}
            />
            <Typography ariaLabel='payment_method_name' variant='body-md-regular'>
              {row.label} .... {row.last4}
            </Typography>
          </div>
        )
      },
    },
  ]

  const isInitialLoading = store.loading && store.payments.length === 0

  return (
    <div className={styles.paymentHistoryContainer}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Typography ariaLabel='payment_history_title' variant='body-lg-medium'>
            Payment history
          </Typography>
        </div>
      </div>

      {isInitialLoading && (
        <div className={styles.loaderContainer}>
          <SpinnerLoader ariaLabel={'PaymentHistory'} />
        </div>
      )}

      {!isInitialLoading && !store.paymentRows.length && !store.hasActiveFilters && (
        <Typography
          className={styles.noPaymentsText}
          ariaLabel='no_payments'
          variant='body-md-regular'
          color='var(--content-primary-tertiary)'
        >
          No payments yet
        </Typography>
      )}

      {!isInitialLoading && (!!store.paymentRows.length || store.hasActiveFilters) && (
        <>
          <div className={styles.filtersContainer}>
            <CalendarDropdown store={store.calendarFilterStore} />
            <Select
              type={'multi'}
              text={'Status'}
              values={store.selectedStatuses}
              onSelectValues={store.setSelectedStatuses}
              items={store.statusDropdownItems}
              btnProps={{ className: styles.statusSelectBtn }}
            />
          </div>
          {store.paymentRows.length > 0 ? (
            <Table
              store={store.tableStore}
              columns={columns}
              rows={store.paymentRows}
              onChangePagination={store.onChangePagination}
              onRowClick={store.onRowClick}
              scrollbarProps={{
                autoHeight: true,
                autoHeightMax: 450,
                viewClassName: '',
              }}
              ariaLabel='PaymentHistoryTable'
            />
          ) : (
            <Typography
              className={styles.noPaymentsText}
              ariaLabel='no_results'
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
            >
              No results found
            </Typography>
          )}
        </>
      )}
    </div>
  )
})
